var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../../sutl';
import { DuplicateChecker, volumeRanges } from './DspConfig';
import { convertNumber, handleTransportError, } from './ParamBridge';
import { ConfigKeyedValueBridge } from './ValueBridge';
class ConversionError extends Error {
}
export class DspBridge {
    constructor(driver, config, controlInfo, muteState, volumeState, volumeRangeState, powerState, transport, valueCallback, log) {
        this.config = config;
        this.controlInfo = controlInfo;
        this.muteState = muteState;
        this.volumeState = volumeState;
        this.volumeRangeState = volumeRangeState;
        this.powerState = powerState;
        this.transport = transport;
        this.valueCallback = valueCallback;
        this.log = log;
        this.isVolumeMuteValid = false;
        if (!driver.mute)
            throw new Error('mute control undefined');
        if (!('getKey' in driver.mute))
            throw new Error('driver does not have dsp mute control');
        if (!driver.volume)
            throw new Error('volume control undefined');
        if (!('getKey' in driver.volume))
            throw new Error('driver does not have dsp volume control');
        this.muteControl = driver.mute;
        this.volumeControl = driver.volume;
        this.duplicateChecker = new DuplicateChecker(config, this.muteControl.getKey);
    }
    setupBridges() {
        const result = { bridges: {}, controls: {} };
        let hasMute = false;
        let hasVolume = false;
        if (this.config.assistiveListening) {
            if (this.config.assistiveListening.mute) {
                result.bridges.assistiveListeningMute = new DspMuteBridge(this, 'assistiveListeningMute', 'assistiveListeningMute', 'assistiveListening', undefined, this.controlInfo.mute, this.config.assistiveListening);
                hasMute = true;
            }
            if (this.config.assistiveListening.volume) {
                result.bridges.assistiveListeningVolume = new DspVolumeBridge(this, 'assistiveListeningVolume', 'assistiveListeningVolume', 'assistiveListening', undefined, this.controlInfo.volume, this.config.assistiveListening);
                hasVolume = true;
            }
        }
        if (this.config.audioInputSources) {
            result.bridges.audioInputMute = new DspMuteBridge(this, 'audioInputMute', 'audioInputMute', 'audioInputSources', 'srcId', this.controlInfo.mute, this.config.audioInputSources);
            result.bridges.audioInputVolume = new DspVolumeBridge(this, 'audioInputVolume', 'audioInputVolume', 'audioInputSources', 'srcId', this.controlInfo.volume, this.config.audioInputSources);
            hasVolume = true;
            hasMute = true;
        }
        if (this.config.mics) {
            result.bridges.mute = new DspMuteBridge(this, 'mute', 'mics', 'mics', undefined, this.controlInfo.mute, this.config.mics, false);
            hasMute = true;
        }
        if (this.config.overflowRooms) {
            result.bridges.overflowRoomMute = new DspMuteBridge(this, 'overflowRoomMute', 'overflowRoomMute', 'overflowRooms', 'roomId', this.controlInfo.mute, this.config.overflowRooms);
            result.bridges.overflowRoomVolume = new DspVolumeBridge(this, 'overflowRoomVolume', 'overflowRoomVolume', 'overflowRooms', 'roomId', this.controlInfo.volume, this.config.overflowRooms);
            hasMute = true;
            hasVolume = true;
        }
        if (this.config.soundReinforcement) {
            result.bridges.soundReinforcementMute = new DspMuteBridge(this, 'soundReinforcementMute', 'soundReinforcementMute', 'soundReinforcement', 'zoneId', this.controlInfo.mute, this.config.soundReinforcement);
            result.bridges.soundReinforcementVolume = new DspVolumeBridge(this, 'soundReinforcementVolume', 'soundReinforcementVolume', 'soundReinforcement', 'zoneId', this.controlInfo.volume, this.config.soundReinforcement);
            hasMute = true;
            hasVolume = true;
        }
        if (this.config.amplifier) {
            result.bridges.volumeMute = new DspMuteBridge(this, 'volumeMute', 'amplifier', 'amplifier', undefined, this.controlInfo.mute, this.config.amplifier, false);
            result.bridges.volume = new DspVolumeBridge(this, 'volume', 'amplifier', 'amplifier', undefined, this.controlInfo.volume, this.config.amplifier, false);
            hasVolume = true;
            hasMute = true;
        }
        if (hasMute)
            result.controls.mute = this.muteControl;
        if (hasVolume)
            result.controls.volume = this.volumeControl;
        return result;
    }
}
export class DspMuteBridge extends ConfigKeyedValueBridge {
    constructor(agg, name, filter, validatedProp, idName, controlInfo, config, compound = true) {
        const key = Sutl.generateFirebaseId();
        const convertedConfig = {};
        if (controlInfo === undefined)
            throw new Error('control info undefined');
        if (idName) {
            for (const [key, cfg] of Object.entries(config)) {
                const ccfg = compound ? cfg.mute : cfg;
                if (ccfg !== undefined)
                    convertedConfig[key] = ccfg;
            }
        }
        else {
            const ccfg = compound ? config.mute : config;
            if (ccfg !== undefined)
                convertedConfig[key] = ccfg;
        }
        if (!agg.muteControl)
            throw new Error('mute control undefined');
        super(name, agg.muteState, agg.muteControl, validatedProp, convertedConfig, controlInfo, agg.powerState, agg.transport, agg.valueCallback, agg.log);
        this.controlIsSubsetOfState = true;
        this._isActive = true;
        this.agg = agg;
        this.compound = compound;
        this.filter = filter;
        this.idName = idName;
        this.key = key;
        this.setFilters[name] = this.getSetFilter();
    }
    get isActive() {
        return this._isActive;
    }
    convertFromParam(eValue) {
        if (this.idName === undefined && typeof eValue === 'boolean') {
            return { key: this.key, value: eValue };
        }
        else if (this.idName !== undefined && typeof eValue === 'object') {
            const key = eValue[this.idName];
            const value = eValue.value;
            if (key !== undefined && typeof value === 'boolean') {
                return { key, value };
            }
        }
        throw new Error(`invalid value: ${JSON.stringify(eValue)}`);
    }
    convertToParam(key, value) {
        if (this.idName === undefined) {
            return value;
        }
        else {
            return { [this.idName]: key, value };
        }
    }
    // Dummy implementation of getInfo. We implement our own validate method, so we don't need it.
    getInfo(config) {
        return { type: 'mute', filter: 'mics', path: [] };
    }
    getSetFilter() {
        const idName = this.idName;
        if (idName === undefined) {
            return (param, value, compParam, compValue) => param !== compParam;
        }
        else {
            return (param, value, compParam, compValue) => {
                const obj = value;
                const compObj = compValue;
                if (param !== compParam)
                    return true;
                return (obj === null || obj === void 0 ? void 0 : obj[idName]) !== (compObj === null || compObj === void 0 ? void 0 : compObj[idName]);
            };
        }
    }
    // We provide our own implementation of the base class validate function. We do this because
    // we want to check duplicates by looking at all the DSP configs rather than just our own
    // config. We also need to convert results for non-collection controls.
    validate() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.validatedProp || !this.control.validate) {
                this.eKeyIsValid = Object.fromEntries(Object.keys(this.keyMap).map(key => [key, true]));
                return undefined;
            }
            // Get validation results and check for duplicates.
            this.buildKeyMap();
            const results = {};
            this.eKeyIsValid = {};
            this.isValid = true;
            for (const key of Object.keys(this.config)) {
                const path = this.idName
                    ? this.compound
                        ? [this.validatedProp, key, 'mute']
                        : [this.validatedProp, key]
                    : this.compound
                        ? [this.validatedProp, 'mute']
                        : [this.validatedProp];
                const info = { type: 'mute', filter: this.filter, path };
                const config = this.config[key];
                let result = yield this.control.validate(config, info);
                if (((_a = this.control.checkForDuplicates) !== null && _a !== void 0 ? _a : true) && result === 'ok') {
                    if (this.agg.duplicateChecker.isDuplicate(config, 'mute')) {
                        result = 'duplicate';
                    }
                }
                if (result === 'ok') {
                    this.eKeyIsValid[key] = true;
                }
                else {
                    this.eKeyIsValid[key] = false;
                    this.isValid = false;
                }
                if (this.compound) {
                    results[key] = { mute: result };
                }
                else {
                    results[key] = result;
                }
            }
            // volumeMute is a special case because (for historical and evolutionary reasons) it
            // doesn't have its own control point -- the amplifier control point is used for both
            // volume and volumeMute. We don't send validation results for volumeMute, but if it
            // doesn't validate, then we want the caps to reflect hasVolumeMute = false.
            if (this.name === 'volumeMute') {
                if (results[this.key] !== 'ok' && results[this.key] !== 'duplicate') {
                    this._isActive = false;
                    this.agg.isVolumeMuteValid = false;
                    delete this.config[this.key];
                    delete this.keyMap[this.key];
                }
                else {
                    this.agg.isVolumeMuteValid = true;
                }
                return {};
            }
            // Convert results structure for non-collection controls.
            if (this.idName === undefined) {
                if (this.validatedProp === undefined)
                    throw new Error('validatedProp undefined');
                if (!(this.key in results))
                    return {};
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return { [this.validatedProp]: results[this.key] };
            }
            return { [this.validatedProp]: results };
        });
    }
}
export class DspVolumeBridge extends ConfigKeyedValueBridge {
    constructor(agg, name, filter, validatedProp, idName, controlInfo, config, compound = true) {
        const key = Sutl.generateFirebaseId();
        const convertedConfig = {};
        if (controlInfo === undefined)
            throw new Error('control info undefined');
        if (idName) {
            for (const [key, cfg] of Object.entries(config)) {
                const ccfg = compound ? cfg.volume : cfg;
                if (ccfg !== undefined)
                    convertedConfig[key] = ccfg;
            }
        }
        else {
            const ccfg = compound ? config.volume : config;
            if (ccfg !== undefined)
                convertedConfig[key] = ccfg;
        }
        if (!agg.volumeControl)
            throw new Error('volume control undefined');
        super(name, agg.volumeState, agg.volumeControl, validatedProp, convertedConfig, controlInfo, agg.powerState, agg.transport, agg.valueCallback, agg.log);
        this.controlIsSubsetOfState = true;
        this.dKey = '';
        this.agg = agg;
        this.compound = compound;
        this.filter = filter;
        this.idName = idName;
        this.key = key;
        this.volumeType = this.validatedProp === 'amplifier' ? 'volume' : 'trim';
        this.setFilters[name] = this.getSetFilter();
    }
    convertFromParam(eValue) {
        if (this.idName === undefined && typeof eValue === 'number') {
            const dKey = this.dKey;
            const to = this.agg.volumeRangeState.values[dKey];
            const from = volumeRanges[this.volumeType];
            if (to === undefined) {
                throw new ConversionError(`${this.name}: no range entry for key "${this.key}"`);
            }
            if (from === undefined) {
                throw new ConversionError(`${this.name}: no volumeRanges entry for volumeType "${this.volumeType}"`);
            }
            if (to.min === 0 && to.max === 0)
                return { key: this.key, value: eValue };
            const dVolume = convertNumber(eValue, from.max, from.min, to.max, to.min, to.round);
            return { key: this.key, value: dVolume };
        }
        else if (this.idName !== undefined && typeof eValue === 'object') {
            const eKey = eValue[this.idName];
            const dKey = this.keyMap[eKey];
            const eVolume = eValue.value;
            const to = this.agg.volumeRangeState.values[dKey];
            const from = volumeRanges[this.volumeType];
            if (to === undefined) {
                throw new ConversionError(`${this.name}: no range entry for key "${eKey}"`);
            }
            if (from === undefined) {
                throw new ConversionError(`${this.name}: no volumeRanges entry for volumeType "${this.volumeType}"`);
            }
            if (to.min === 0 && to.max === 0)
                return { key: this.key, value: eVolume };
            const dVolume = convertNumber(eVolume, from.max, from.min, to.max, to.min, to.round);
            if (eKey !== undefined && typeof dVolume === 'number' && !Number.isNaN(dVolume)) {
                return { key: eKey, value: dVolume };
            }
        }
        throw new Error(`invalid value: ${JSON.stringify(eValue)}`);
    }
    convertToParam(eKey, dVolume) {
        if (this.idName === undefined) {
            const from = this.agg.volumeRangeState.values[this.dKey];
            const to = volumeRanges[this.volumeType];
            if (from === undefined) {
                throw new ConversionError(`${this.name}: no range entry for key "${this.key}"`);
            }
            if (to === undefined) {
                throw new ConversionError(`${this.name}: no volumeRanges entry for volumeType "${this.volumeType}"`);
            }
            if (from.min === 0 && from.max === 0)
                return dVolume;
            return convertNumber(dVolume, from.max, from.min, to.max, to.min, 1);
        }
        else {
            const dKey = this.keyMap[eKey];
            const from = this.agg.volumeRangeState.values[dKey];
            const to = volumeRanges[this.volumeType];
            if (from === undefined) {
                throw new ConversionError(`${this.name}: no range entry for key "${eKey}"`);
            }
            if (to === undefined) {
                throw new ConversionError(`${this.name}: no volumeRanges entry for volumeType "${this.volumeType}"`);
            }
            if (from.min === 0 && from.max === 0) {
                return { [this.idName]: eKey, value: dVolume };
            }
            const eVolume = convertNumber(dVolume, from.max, from.min, to.max, to.min, 1);
            return { [this.idName]: eKey, value: eVolume };
        }
    }
    // Dummy implementation of getInfo. We implement our own validate method, so we don't need it.
    getInfo(config) {
        return { type: 'volume', filter: 'amplifier', path: [], volumeType: 'volume' };
    }
    getSetFilter() {
        const idName = this.idName;
        if (idName === undefined) {
            return (param, value, compParam, compValue) => param !== compParam;
        }
        else {
            return (param, value, compParam, compValue) => {
                const obj = value;
                const compObj = compValue;
                if (param !== compParam)
                    return true;
                return (obj === null || obj === void 0 ? void 0 : obj[idName]) !== (compObj === null || compObj === void 0 ? void 0 : compObj[idName]);
            };
        }
    }
    onSet(param, setValue) {
        const _super = Object.create(null, {
            onSet: { get: () => super.onSet }
        });
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield _super.onSet.call(this, param, setValue);
            }
            catch (error) {
                if (error instanceof ConversionError) {
                    this.log.warning(error.message);
                }
                else {
                    throw error;
                }
            }
            if (!this.controlInfo.canSet(this.powerState.value))
                return; // super handles response
            // volumeMute is a special case because (for historical and evolutionary reasons) it
            // doesn't have its own control point -- the amplifier control point is used for both
            // volume and volumeMute. If the amplifier control validated for mute, then the
            // muteControl will be defined, and we handle the mute sync between volume and volumeMute.
            if (this.agg.muteControl && this.agg.isVolumeMuteValid && param === 'volume') {
                const dKey = this.keyMap[this.key];
                const newMute = this.agg.volumeState.values[dKey] <= this.agg.volumeRangeState.values[dKey].min;
                if (newMute !== this.agg.muteState.values[dKey]) {
                    const tags = ['poll', 'set.volumeMute'];
                    this.transport.removeCommands(tags);
                    try {
                        const newValue = yield this.agg.muteControl.set(dKey, newMute, [
                            'set.VolumeMute',
                        ]);
                        this.agg.muteState.update(dKey, newValue);
                    }
                    catch (error) {
                        handleTransportError(this.log, error);
                    }
                }
            }
        });
    }
    // We provide our own implementation of the base class validate function. We do this because
    // we want to check duplicates by looking at all the DSP configs rather than just our own
    // config. We also need to convert results for non-collection controls.
    validate() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.validatedProp || !this.control.validate) {
                this.eKeyIsValid = Object.fromEntries(Object.keys(this.keyMap).map(key => [key, true]));
                return undefined;
            }
            // Get validation results and check for duplicates.
            this.buildKeyMap();
            if (this.idName === undefined) {
                if (this.agg.volumeControl === undefined) {
                    throw new Error('volume control undefined');
                }
                this.dKey = this.agg.volumeControl.getKey(this.config[this.key]);
            }
            const results = {};
            this.eKeyIsValid = {};
            this.isValid = true;
            for (const key of Object.keys(this.config)) {
                const path = this.idName
                    ? this.compound
                        ? [this.validatedProp, key, 'volume']
                        : [this.validatedProp, key]
                    : this.compound
                        ? [this.validatedProp, 'volume']
                        : [this.validatedProp];
                const info = {
                    type: 'volume',
                    filter: this.filter,
                    path,
                    volumeType: this.volumeType,
                };
                const config = this.config[key];
                const dKey = this.idName === undefined ? this.dKey : this.keyMap[key];
                let result = yield this.control.validate(config, info);
                if (((_a = this.control.checkForDuplicates) !== null && _a !== void 0 ? _a : true) && result === 'ok') {
                    if (this.agg.duplicateChecker.isDuplicate(config, 'volume')) {
                        result = 'duplicate';
                    }
                }
                if (result === 'ok') {
                    this.eKeyIsValid[key] = true;
                }
                else {
                    this.eKeyIsValid[key] = false;
                    this.isValid = false;
                }
                if (this.compound) {
                    results[key] = { volume: result };
                }
                else {
                    results[key] = result;
                }
                if (this.isValid) {
                    this.agg.volumeRangeState.update(dKey, typeof ((_b = this.agg.volumeControl) === null || _b === void 0 ? void 0 : _b.range) === 'function'
                        ? yield this.agg.volumeControl.range(config, info)
                        : (_d = (_c = this.agg.volumeControl) === null || _c === void 0 ? void 0 : _c.range) !== null && _d !== void 0 ? _d : { max: 0, min: 0, round: 1 });
                }
            }
            // Convert results structure for non-collection controls.
            if (this.idName === undefined) {
                if (this.validatedProp === undefined)
                    throw new Error('validatedProp undefined');
                if (!(this.key in results))
                    return {};
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return { [this.validatedProp]: results[this.key] };
            }
            return { [this.validatedProp]: results };
        });
    }
}
